import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import FundUseDisclosure from '../../../components/giving/FundUseDisclosure'
import FeeDisclosure from '../../../components/giving/FeeDisclosure'
import YearEndStatements from './YearEndStatements'
import { Loader2, Plus, CheckCircle, XCircle, Calendar, CreditCard, Pause, Play, Trash2, Edit, DollarSign, X, Church, AlertCircle, RefreshCw, TrendingUp, ArrowRight, ChevronUp, ChevronDown, Download, Filter } from 'lucide-react'

const VISIBLE_DONATION_COUNT = 3

function StatCard({ label, value, subtitle }) {
  return (
    <div className="bg-surface-linen rounded p-4">
      <div className="text-xs text-charcoal-muted mb-1 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-charcoal">{value}</div>
      {subtitle && <div className="text-xs text-charcoal-muted mt-1">{subtitle}</div>}
    </div>
  )
}

function BreakdownBar({ label, amount, count, total, formatCurrency }) {
  const pct = total > 0 ? (amount / total) * 100 : 0
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-charcoal-muted truncate">{label}</span>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <span className="font-semibold text-charcoal">{formatCurrency(amount)}</span>
          <span className="text-charcoal-muted text-xs">({count})</span>
        </div>
      </div>
      <div className="w-full bg-surface-border/40 rounded-full h-1.5">
        <div className="bg-gold h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export default function DonorDonations() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Existing state
  const [loading, setLoading] = useState(true)
  const [userParishes, setUserParishes] = useState([])
  const [selectedParishId, setSelectedParishId] = useState('')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [donations, setDonations] = useState([])
  const [donationsLoading, setDonationsLoading] = useState(true)
  const [donationsPage, setDonationsPage] = useState(1)
  const [donationsMeta, setDonationsMeta] = useState(null)
  const [recurringDonations, setRecurringDonations] = useState([])
  const [recurringDonationsLoading, setRecurringDonationsLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [successMessageType, setSuccessMessageType] = useState('created')
  const [showDonateModal, setShowDonateModal] = useState(false)
  const [expandedType, setExpandedType] = useState(null)
  const [showAllDonations, setShowAllDonations] = useState(false)
  const expandedSectionRef = useRef(null)

  // Stats & filter state
  const [dateRange, setDateRange] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [customRangeSubmitKey, setCustomRangeSubmitKey] = useState(0)
  const [donationTypeFilter, setDonationTypeFilter] = useState('all')
  const [stats, setStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Load parishes and recurring donations on mount
  useEffect(() => {
    if (user?.id) {
      fetchUserParishes()
      fetchRecurringDonations()
    }
  }, [user?.id])

  // Reload stats when filter changes (and on mount)
  useEffect(() => {
    if (user?.id) fetchStats()
  }, [user?.id, dateRange, customRangeSubmitKey, donationTypeFilter])

  // Reload history when page or filter changes (and on mount)
  useEffect(() => {
    if (user?.id) fetchDonations()
  }, [user?.id, donationsPage, dateRange, customRangeSubmitKey, donationTypeFilter])

  // Success message from recurring donation setup
  useEffect(() => {
    const recurringParam = searchParams.get('recurring')
    if (recurringParam === 'success' || recurringParam === 'updated') {
      setSuccessMessageType(recurringParam === 'updated' ? 'updated' : 'created')
      setShowSuccessMessage(true)
      setSearchParams({}, { replace: true })
      setTimeout(() => setShowSuccessMessage(false), 5000)
    }
  }, [searchParams, setSearchParams])

  // Open donate modal when navigating from parish detail page
  useEffect(() => {
    const donateParishId = searchParams.get('donate')
    if (donateParishId && !loading) {
      setSelectedParishId(donateParishId)
      setShowDonateModal(true)
      setSearchParams({}, { replace: true })
      // Donating from the page of a parish the donor hasn't added: list it
      // too, so the modal shows where the gift is going.
      if (!userParishes.some((p) => p.parishId === donateParishId)) {
        loadParishDetails({ parishId: donateParishId }).then((target) => {
          setUserParishes((prev) =>
            prev.some((p) => p.parishId === donateParishId) ? prev : [target, ...prev]
          )
          if (!target.stripeReady) setSelectedParishId('')
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading])

  useEffect(() => {
    if (expandedType && expandedSectionRef.current) {
      expandedSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [expandedType])

  const getDateRangeParams = () => {
    if (dateRange === 'all') return {}
    if (dateRange === 'custom') {
      return {
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
      }
    }
    const now = new Date()
    let start
    if (dateRange === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    } else if (dateRange === 'week') {
      start = new Date(now)
      start.setDate(now.getDate() - now.getDay())
      start.setHours(0, 0, 0, 0)
    } else if (dateRange === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1)
    } else if (dateRange === 'year') {
      start = new Date(now.getFullYear(), 0, 1)
    } else {
      return {}
    }
    return { startDate: start.toISOString(), endDate: now.toISOString() }
  }

  const fetchStats = async () => {
    if (!user?.id) return
    setStatsLoading(true)
    try {
      const dateParams = getDateRangeParams()
      const typeParam = donationTypeFilter !== 'all' ? donationTypeFilter : undefined
      let allDonations = []
      let page = 1
      let hasMore = true
      while (hasMore) {
        const response = await api.getUserDonations(user.id, { page, limit: 100, donationType: typeParam, ...dateParams })
        allDonations = allDonations.concat(response.data || [])
        hasMore = response.meta?.hasNextPage || false
        page++
      }

      // Amounts are net of refunds and span succeeded + refunded rows; counts
      // cover only gifts that are not fully refunded (status 'succeeded').
      const netOf = d => (d.amount || 0) - (d.refundedAmount || 0)
      const counted = allDonations.filter(d => d.status === 'succeeded' || d.status === 'refunded')
      const succeeded = counted.filter(d => d.status === 'succeeded')
      const totalAmount = counted.reduce((sum, d) => sum + netOf(d), 0)
      const totalCount = succeeded.length
      const avgAmount = totalCount > 0 ? Math.round(totalAmount / totalCount) : 0
      const maxAmount = counted.length > 0 ? Math.max(...counted.map(netOf)) : 0

      const oneTimeDonations = counted.filter(d => d.donationType === 'one-time')
      const recurringDons = counted.filter(d => d.donationType === 'recurring')
      const roundupDons = counted.filter(d => d.donationType === 'roundup')

      // This month / this year (always computed from the fetched set)
      const now = new Date()
      const thisMonthAmount = counted
        .filter(d => {
          const date = new Date(d.completedAt || d.createdAt)
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()
        })
        .reduce((sum, d) => sum + netOf(d), 0)
      const thisYearAmount = counted
        .filter(d => new Date(d.completedAt || d.createdAt).getFullYear() === now.getFullYear())
        .reduce((sum, d) => sum + netOf(d), 0)

      // Group by parish
      const parishMap = {}
      for (const d of counted) {
        const name = d.parishName || 'Unknown Parish'
        if (!parishMap[name]) parishMap[name] = { amount: 0, count: 0 }
        parishMap[name].amount += netOf(d)
        if (d.status === 'succeeded') parishMap[name].count++
      }
      const byParish = Object.entries(parishMap)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.amount - a.amount)

      setStats({
        totalAmount,
        totalCount,
        avgAmount,
        maxAmount,
        oneTimeCount: oneTimeDonations.filter(d => d.status === 'succeeded').length,
        oneTimeAmount: oneTimeDonations.reduce((sum, d) => sum + netOf(d), 0),
        recurringCount: recurringDons.filter(d => d.status === 'succeeded').length,
        recurringAmount: recurringDons.reduce((sum, d) => sum + netOf(d), 0),
        roundupCount: roundupDons.filter(d => d.status === 'succeeded').length,
        roundupAmount: roundupDons.reduce((sum, d) => sum + netOf(d), 0),
        byParish,
        thisMonthAmount,
        thisYearAmount,
      })
    } catch (err) {
      console.error('Failed to fetch donation stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }

  const loadParishDetails = async (parish) => {
    try {
      const fullParish = await api.getParish(parish.parishId)
      const givingUnavailable = fullParish.giving?.mode === 'unavailable'
      let stripeReady = false
      if (!givingUnavailable) {
        try {
          const result = await api.stripeIsReady(fullParish.id)
          stripeReady = result.isReady
        } catch { /* assume not ready */ }
      }
      return { ...parish, name: fullParish.name, stripeReady, givingUnavailable, fundUse: fullParish.giving?.fundUse, fee: fullParish.giving?.fee }
    } catch (err) {
      return { ...parish, name: `Parish ${parish.parishId}`, stripeReady: false }
    }
  }

  const fetchUserParishes = async () => {
    try {
      setLoading(true)
      setError(null)
      const userData = await api.getUser()
      const parishes = userData.parishes || []
      const parishDetails = await Promise.all(parishes.map(loadParishDetails))
      setUserParishes(parishDetails)
      if (!selectedParishId && parishDetails.length > 0) {
        const firstReady = parishDetails.find((p) => p.stripeReady)
        if (firstReady) setSelectedParishId(firstReady.parishId)
      }
    } catch (err) {
      setError(err.message || 'Failed to load parishes')
    } finally {
      setLoading(false)
    }
  }

  const fetchDonations = async () => {
    if (!user?.id) return
    try {
      setDonationsLoading(true)
      const dateParams = getDateRangeParams()
      const typeParam = donationTypeFilter !== 'all' ? donationTypeFilter : undefined
      const response = await api.getUserDonations(user.id, { page: donationsPage, limit: 20, donationType: typeParam, ...dateParams })
      setDonations(response.data || [])
      setDonationsMeta(response.meta || null)
    } catch (err) {
      console.error('Failed to fetch donations:', err)
    } finally {
      setDonationsLoading(false)
    }
  }

  const fetchRecurringDonations = async () => {
    if (!user?.id) return
    try {
      setRecurringDonationsLoading(true)
      const data = await api.getUserRecurringDonations()
      setRecurringDonations(data || [])
    } catch (err) {
      console.error('Failed to fetch recurring donations:', err)
    } finally {
      setRecurringDonationsLoading(false)
    }
  }

  const handlePauseRecurring = async (id) => {
    try {
      setActionLoading(id)
      await api.pauseRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      alert(err.message || 'Failed to pause donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleResumeRecurring = async (id) => {
    try {
      setActionLoading(id)
      await api.resumeRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      alert(err.message || 'Failed to resume donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancelRecurring = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this recurring donation? This action cannot be undone.')) return
    try {
      setActionLoading(id)
      await api.cancelRecurringDonation(id)
      await fetchRecurringDonations()
    } catch (err) {
      alert(err.message || 'Failed to cancel donation')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDonate = async (e) => {
    e.preventDefault()
    if (!selectedParishId) { setError('Please select a parish'); return }
    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum < 0.5) { setError('Please enter a valid amount (minimum $0.50)'); return }
    try {
      setSubmitting(true)
      setError(null)
      navigate(`/donate/${selectedParishId}?amount=${amountNum}`)
    } catch (err) {
      setError(err.message || 'Failed to initiate donation')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDateRangeChange = (range) => {
    setDateRange(range)
    setDonationsPage(1)
    setShowAllDonations(false)
    if (range !== 'custom') {
      setStartDate('')
      setEndDate('')
      setCustomRangeSubmitKey(0)
    }
  }

  const handleCustomRangeSubmit = () => {
    if (!startDate || !endDate) { alert('Please select both start and end dates'); return }
    if (new Date(startDate) > new Date(endDate)) { alert('Start date must be before or equal to end date'); return }
    setCustomRangeSubmitKey(prev => prev + 1)
    setDonationsPage(1)
    setShowAllDonations(false)
  }

  const clearAllFilters = () => {
    setDateRange('all')
    setStartDate('')
    setEndDate('')
    setCustomRangeSubmitKey(0)
    setDonationTypeFilter('all')
    setDonationsPage(1)
    setShowAllDonations(false)
  }

  const exportToCSV = async () => {
    if (!user?.id) return
    try {
      setExporting(true)
      const dateParams = getDateRangeParams()
      const typeParam = donationTypeFilter !== 'all' ? donationTypeFilter : undefined
      let allDonations = []
      let page = 1
      let hasMore = true
      while (hasMore) {
        const response = await api.getUserDonations(user.id, { page, limit: 100, donationType: typeParam, ...dateParams })
        allDonations = allDonations.concat(response.data || [])
        hasMore = response.meta?.hasNextPage || false
        page++
      }

      if (allDonations.length === 0) {
        alert('No donations found for the selected date range')
        return
      }

      allDonations.sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt) : new Date(a.createdAt)
        const dateB = b.completedAt ? new Date(b.completedAt) : new Date(b.createdAt)
        return dateB - dateA
      })

      const headers = ['Date', 'Parish', 'Amount', 'Type', 'Status', 'Payment Method']
      const rows = allDonations.map(d => [
        formatDate(d.completedAt || d.createdAt),
        d.parishName || 'Unknown',
        formatCurrency(d.amount),
        d.donationType === 'recurring' ? 'Recurring' : d.donationType === 'roundup' ? 'Round-Up' : 'One-Time',
        d.status,
        d.paymentMethodLast4 ? `•••• ${d.paymentMethodLast4}` : 'N/A',
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      let filename = `my-donations-${new Date().toISOString().split('T')[0]}`
      if (dateParams.startDate || dateParams.endDate) {
        const startStr = dateParams.startDate ? new Date(dateParams.startDate).toISOString().split('T')[0] : 'all'
        const endStr = dateParams.endDate ? new Date(dateParams.endDate).toISOString().split('T')[0] : 'all'
        filename = `my-donations-${startStr}-to-${endStr}`
      }
      a.download = `${filename}.csv`

      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      alert(`Failed to export: ${err.message || 'Unknown error'}`)
    } finally {
      setExporting(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'text-green-500'
      case 'failed': return 'text-red-600'
      case 'pending': case 'processing': return 'text-yellow-500'
      default: return 'text-charcoal-muted'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded': return <CheckCircle className="w-4 h-4" />
      case 'failed': return <XCircle className="w-4 h-4" />
      default: return null
    }
  }

  const getRecurringStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200'
      case 'paused': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'canceled': return 'text-red-600 bg-red-50 border-red-200'
      case 'past_due': return 'text-orange-600 bg-orange-50 border-orange-200'
      default: return 'text-charcoal-muted bg-surface-linen border-surface-border'
    }
  }

  const getRecurringStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active'
      case 'paused': return 'Paused'
      case 'canceled': return 'Canceled'
      case 'past_due': return 'Past Due'
      default: return status
    }
  }

  const activeRecurring = recurringDonations.filter(d => d.status !== 'canceled')

  return (
    <div>
      <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Donations</h1>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-green-700 font-semibold mb-1">
                {successMessageType === 'updated' ? 'Recurring Donation Updated!' : 'Recurring Donation Created!'}
              </h3>
              <p className="text-green-700 text-sm">
                {successMessageType === 'updated'
                  ? 'Changes will take effect on your next billing cycle.'
                  : 'Your recurring donation has been set up successfully.'}
              </p>
            </div>
            <button onClick={() => setShowSuccessMessage(false)} className="text-green-700 hover:text-green-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Donation Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

        {/* One Time Card */}
        <button
          onClick={() => setShowDonateModal(true)}
          className="text-left rounded p-6 border transition-all group flex flex-col bg-white border-surface-border hover:border-gold/40 hover:shadow-subtle"
        >
          <div className="w-12 h-12 border border-gold/30 rounded flex items-center justify-center mb-4 group-hover:border-gold/60 transition-colors">
            <DollarSign className="w-6 h-6 text-gold" />
          </div>
          <h3 className="text-charcoal font-bold font-display text-lg mb-1">One Time</h3>
          <p className="text-charcoal-muted text-sm mb-4">Make a single donation to your parish, processed securely through Stripe.</p>
          <ul className="text-sm text-charcoal-subtle space-y-1 mb-5 flex-1">
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Any amount from $0.50</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Instant processing</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Secure via Stripe</li>
          </ul>
          <span className="text-sm font-semibold flex items-center gap-1 transition-colors text-charcoal-muted group-hover:text-gold">
            Donate now <ArrowRight className="w-4 h-4" />
          </span>
        </button>

        {/* Recurring Card */}
        <button
          onClick={() => setExpandedType(expandedType === 'recurring' ? null : 'recurring')}
          className={`text-left rounded p-6 border transition-all group flex flex-col ${
            expandedType === 'recurring'
              ? 'bg-gold/5 border-gold/50'
              : 'bg-white border-surface-border hover:border-gold/40 hover:shadow-subtle'
          }`}
        >
          <div className={`w-12 h-12 border rounded flex items-center justify-center mb-4 transition-colors ${
            expandedType === 'recurring' ? 'border-gold/60 bg-gold/10' : 'border-gold/30 group-hover:border-gold/60'
          }`}>
            <RefreshCw className="w-6 h-6 text-gold" />
          </div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-charcoal font-bold font-display text-lg">Recurring</h3>
            {activeRecurring.length > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gold/15 text-gold">
                {activeRecurring.length} active
              </span>
            )}
          </div>
          <p className="text-charcoal-muted text-sm mb-4">Set up automatic periodic giving so you never miss a contribution.</p>
          <ul className="text-sm text-charcoal-subtle space-y-1 mb-5 flex-1">
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Weekly or monthly</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Pause or cancel anytime</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Automatic billing</li>
          </ul>
          <span className={`text-sm font-semibold flex items-center gap-1 transition-colors ${
            expandedType === 'recurring' ? 'text-gold' : 'text-charcoal-muted group-hover:text-gold'
          }`}>
            {expandedType === 'recurring' ? 'Hide' : 'Manage'} <ArrowRight className="w-4 h-4" />
          </span>
        </button>

        {/* Round-Up Card */}
        <button
          onClick={() => navigate('/app/donor/roundups')}
          className="text-left bg-white border border-surface-border rounded p-6 hover:border-gold/40 hover:shadow-subtle transition-all group flex flex-col"
        >
          <div className="w-12 h-12 border border-gold/30 rounded flex items-center justify-center mb-4 group-hover:border-gold/60 transition-colors">
            <TrendingUp className="w-6 h-6 text-gold" />
          </div>
          <h3 className="text-charcoal font-bold font-display text-lg mb-1">Round-Up</h3>
          <p className="text-charcoal-muted text-sm mb-4">Automatically donate the spare change from everyday purchases.</p>
          <ul className="text-sm text-charcoal-subtle space-y-1 mb-5 flex-1">
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Links to your bank account</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Rounds up each purchase</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" /> Enable or disable anytime</li>
          </ul>
          <span className="text-sm font-semibold text-charcoal-muted group-hover:text-gold flex items-center gap-1 transition-colors">
            Manage <ArrowRight className="w-4 h-4" />
          </span>
        </button>
      </div>

      {/* One-Time Donation Modal */}
      {showDonateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowDonateModal(false); setError(null) } }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-surface-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 border border-gold/40 rounded flex items-center justify-center bg-gold/10">
                  <DollarSign className="w-5 h-5 text-gold" />
                </div>
                <h2 className="text-xl font-semibold font-display text-charcoal">One-Time Donation</h2>
              </div>
              <button
                onClick={() => { setShowDonateModal(false); setError(null) }}
                className="text-charcoal-muted hover:text-charcoal transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gold animate-spin" />
                </div>
              ) : userParishes.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-charcoal-muted mb-4">You haven't added any parishes yet.</p>
                  <Button onClick={() => { setShowDonateModal(false); navigate('/app/donor/parish') }}>
                    <Plus className="w-4 h-4 mr-2" /> Find Your Parish
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleDonate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2">Select Parish</label>
                    <div className="flex flex-col gap-2">
                      {userParishes.map((parish) => (
                        <div key={parish.parishId} className="relative group/parish">
                          <button
                            type="button"
                            onClick={() => parish.stripeReady && setSelectedParishId(parish.parishId)}
                            disabled={!parish.stripeReady}
                            className={`flex items-center gap-3 w-full rounded px-4 py-3 text-left transition-all ${
                              !parish.stripeReady
                                ? 'bg-surface-linen border border-surface-border cursor-not-allowed opacity-50'
                                : selectedParishId === parish.parishId
                                ? 'bg-gold/10 border border-gold/40'
                                : 'bg-white border border-surface-border hover:border-gold/30 hover:bg-surface-linen'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                              selectedParishId === parish.parishId ? 'bg-gold/20' : 'bg-charcoal/5'
                            }`}>
                              <Church className={`w-4 h-4 ${
                                !parish.stripeReady ? 'text-charcoal-faint' : selectedParishId === parish.parishId ? 'text-gold' : 'text-charcoal-faint'
                              }`} />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className={`text-sm font-medium ${
                                !parish.stripeReady ? 'text-charcoal-faint' : selectedParishId === parish.parishId ? 'text-charcoal' : 'text-charcoal-muted'
                              }`}>
                                {parish.name || `Parish ${parish.parishId}`}
                              </span>
                              {!parish.stripeReady && (
                                <span className="text-[11px] text-red-600/70 flex items-center gap-1 mt-0.5">
                                  <AlertCircle className="w-3 h-3 shrink-0" /> {parish.givingUnavailable ? "Isn't accepting donations right now" : 'Payment setup incomplete'}
                                </span>
                              )}
                            </div>
                            {parish.stripeReady && selectedParishId === parish.parishId && (
                              <CheckCircle className="w-4 h-4 text-gold ml-auto shrink-0" />
                            )}
                          </button>
                          {!parish.stripeReady && (
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-white border border-surface-border rounded shadow-subtle opacity-0 group-hover/parish:opacity-100 transition-opacity pointer-events-none z-50 w-64 text-center">
                              <p className="text-charcoal-muted text-xs">{parish.givingUnavailable ? "This parish isn't accepting donations right now." : "This parish hasn't completed Stripe onboarding and cannot receive donations."}</p>
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-white border-r border-b border-surface-border rotate-45 -mt-1" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-charcoal mb-2">Donation Amount ($)</label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0.5"
                      value={amount}
                      onChange={(e) => { setAmount(e.target.value); setError(null) }}
                      placeholder="0.00"
                      required
                      className="bg-surface-linen border-surface-border text-charcoal"
                    />
                    <p className="text-xs text-charcoal-muted mt-1">Minimum donation: $0.50</p>
                  </div>

                  {(() => {
                    const selected = userParishes.find((p) => p.parishId === selectedParishId)
                    return selected && selected.stripeReady ? (
                      <>
                        <FundUseDisclosure parishName={selected.name} fundUse={selected.fundUse} />
                        <FeeDisclosure
                          percent={selected.fee?.oneTimePercent}
                          fixedCents={selected.fee?.fixedCents}
                          parishName={selected.name}
                        />
                      </>
                    ) : null
                  })()}

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowDonateModal(false); setError(null) }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-gold text-black font-semibold hover:bg-gold/90"
                    >
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : 'Continue to Payment'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expanded Recurring Panel */}
      {expandedType === 'recurring' && (
        <div ref={expandedSectionRef} className="bg-white border border-gold/30 rounded p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold font-display text-charcoal">Recurring Donations</h2>
            <Button
              size="sm"
              onClick={() => navigate(selectedParishId ? `/app/donor/recurring-donation-setup?parishId=${selectedParishId}` : '/app/donor/recurring-donation-setup')}
              className="bg-gold text-black font-semibold hover:bg-gold/90"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New
            </Button>
          </div>

          {recurringDonationsLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-gold animate-spin" />
            </div>
          ) : recurringDonations.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-charcoal-muted mb-4">You don't have any recurring donations yet.</p>
              {userParishes.length > 0 ? (
                <Button
                  variant="outline"
                  onClick={() => navigate(selectedParishId ? `/app/donor/recurring-donation-setup?parishId=${selectedParishId}` : '/app/donor/recurring-donation-setup')}
                >
                  <Plus className="w-4 h-4 mr-2" /> Set Up Recurring Donation
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/app/donor/parish')}>
                  <Plus className="w-4 h-4 mr-2" /> Find Your Parish
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {recurringDonations.map((donation) => (
                <div
                  key={donation.id}
                  className="border border-surface-border rounded p-4 hover:border-gold/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-base font-semibold text-charcoal">
                          {donation.parish?.name || 'Unknown Parish'}
                        </h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${getRecurringStatusColor(donation.status)}`}>
                          {getRecurringStatusLabel(donation.status)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-charcoal-muted">
                        <span className="font-semibold text-charcoal">
                          {formatCurrency(donation.amountCents)}
                          <span className="font-normal text-charcoal-subtle ml-1">
                            {donation.interval === 'weekly' ? '/ week' : '/ month'}
                          </span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Started {formatDate(donation.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {donation.status === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePauseRecurring(donation.id)}
                            disabled={actionLoading === donation.id}
                            title="Pause"
                          >
                            {actionLoading === donation.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/donor/recurring-donations/${donation.id}/edit`)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelRecurring(donation.id)}
                            disabled={actionLoading === donation.id}
                            className="text-red-600 hover:text-red-600 hover:border-red-400"
                            title="Cancel"
                          >
                            {actionLoading === donation.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </>
                      )}
                      {donation.status === 'paused' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResumeRecurring(donation.id)}
                            disabled={actionLoading === donation.id}
                            title="Resume"
                          >
                            {actionLoading === donation.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelRecurring(donation.id)}
                            disabled={actionLoading === donation.id}
                            className="text-red-600 hover:text-red-600 hover:border-red-400"
                            title="Cancel"
                          >
                            {actionLoading === donation.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </>
                      )}
                      {donation.status === 'past_due' && (
                        <span className="text-orange-500 text-sm font-medium">Payment Failed</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Giving Stats */}
      <div className="bg-white border border-surface-border rounded p-6 mb-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-semibold font-display text-charcoal">My Giving</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-charcoal-muted" />
              <select
                value={donationTypeFilter}
                onChange={(e) => { setDonationTypeFilter(e.target.value); setDonationsPage(1); setShowAllDonations(false) }}
                className="px-3 py-2 bg-white border border-surface-border rounded text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="all">All Types</option>
                <option value="one-time">One-Time</option>
                <option value="recurring">Recurring</option>
                <option value="roundup">Round-Up</option>
              </select>
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="px-3 py-2 bg-white border border-surface-border rounded text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
              {(dateRange !== 'all' || donationTypeFilter !== 'all') && (
                <Button onClick={clearAllFilters} variant="outline" size="sm" className="flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear
                </Button>
              )}
            </div>
            <Button
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={exporting}
            >
              {exporting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Exporting...</>
              ) : (
                <><Download className="w-4 h-4" /> Export CSV</>
              )}
            </Button>
          </div>
        </div>

        {/* Custom Date Range Inputs */}
        {dateRange === 'custom' && (
          <div className="bg-surface-linen rounded p-4 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px]">
                <label className="block text-sm font-medium text-charcoal mb-2">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white border-surface-border text-charcoal"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-sm font-medium text-charcoal mb-2">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-white border-surface-border text-charcoal"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <Button onClick={handleCustomRangeSubmit} disabled={!startDate || !endDate}>
                Apply
              </Button>
            </div>
          </div>
        )}

        {/* Stats Content */}
        {statsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : !stats || stats.totalCount === 0 ? (
          <div className="text-center py-8">
            <p className="text-charcoal-muted text-sm">
              {(dateRange !== 'all' || donationTypeFilter !== 'all')
                ? 'No completed donations found for the selected filters.'
                : 'No donations yet. Make your first donation above!'}
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="Total Donated"
                value={formatCurrency(stats.totalAmount)}
                subtitle={`${stats.totalCount} ${stats.totalCount === 1 ? 'donation' : 'donations'}`}
              />
              {dateRange === 'all' ? (
                <>
                  <StatCard label="This Month" value={formatCurrency(stats.thisMonthAmount)} />
                  <StatCard label="This Year" value={formatCurrency(stats.thisYearAmount)} />
                </>
              ) : (
                <>
                  <StatCard label="Avg Gift" value={formatCurrency(stats.avgAmount)} />
                  <StatCard label="Largest Gift" value={formatCurrency(stats.maxAmount)} />
                </>
              )}
              <StatCard
                label={dateRange === 'all' ? 'Avg Gift' : 'Donations'}
                value={dateRange === 'all' ? formatCurrency(stats.avgAmount) : stats.totalCount}
              />
            </div>

            {/* Breakdown */}
            <div className={`grid grid-cols-1 gap-6 pt-6 border-t border-surface-border ${stats.byParish.length > 1 ? 'md:grid-cols-2' : ''}`}>
              {/* By Type */}
              <div>
                <h3 className="text-sm font-semibold text-charcoal mb-3">By Type</h3>
                <div className="space-y-3">
                  <BreakdownBar
                    label="One-Time"
                    amount={stats.oneTimeAmount}
                    count={stats.oneTimeCount}
                    total={stats.totalAmount}
                    formatCurrency={formatCurrency}
                  />
                  <BreakdownBar
                    label="Recurring"
                    amount={stats.recurringAmount}
                    count={stats.recurringCount}
                    total={stats.totalAmount}
                    formatCurrency={formatCurrency}
                  />
                  {stats.roundupCount > 0 && (
                    <BreakdownBar
                      label="Round-Up"
                      amount={stats.roundupAmount}
                      count={stats.roundupCount}
                      total={stats.totalAmount}
                      formatCurrency={formatCurrency}
                    />
                  )}
                </div>
              </div>

              {/* By Parish — only shown when donating to multiple parishes */}
              {stats.byParish.length > 1 && (
                <div>
                  <h3 className="text-sm font-semibold text-charcoal mb-3">By Parish</h3>
                  <div className="space-y-3">
                    {stats.byParish.map(parish => (
                      <BreakdownBar
                        key={parish.name}
                        label={parish.name}
                        amount={parish.amount}
                        count={parish.count}
                        total={stats.totalAmount}
                        formatCurrency={formatCurrency}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Donation History */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold font-display text-charcoal">
            Donation History
            {(dateRange !== 'all' || donationTypeFilter !== 'all') && (
              <span className="ml-2 text-sm font-normal text-charcoal-muted">(filtered)</span>
            )}
          </h2>
        </div>

        {donationsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : donations.length === 0 ? (
          <div className="text-center py-8">
            {(dateRange !== 'all' || donationTypeFilter !== 'all') ? (
              <p className="text-charcoal-muted">No donations found for the selected filters.</p>
            ) : (
              <>
                <p className="text-charcoal-muted mb-2">You haven't made any donations yet.</p>
                <p className="text-charcoal-subtle text-sm">Your donation history will appear here after your first donation.</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="relative">
              <div className="space-y-3">
                {(showAllDonations ? donations : donations.slice(0, VISIBLE_DONATION_COUNT)).map((donation) => (
                  <div
                    key={donation.id}
                    className="bg-white border border-surface-border rounded p-4 hover:border-gold/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-base font-semibold text-charcoal">{donation.parishName}</h3>
                          <span className={`text-sm font-medium flex items-center gap-1 ${getStatusColor(donation.status)}`}>
                            {getStatusIcon(donation.status)}
                            {donation.status.charAt(0).toUpperCase() + donation.status.slice(1)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2 text-charcoal-muted">
                            <CreditCard className="w-4 h-4" />
                            <span className="font-semibold text-charcoal">{formatCurrency(donation.amount)}</span>
                            {donation.paymentMethodLast4 && (
                              <span className="text-charcoal-subtle">•••• {donation.paymentMethodLast4}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-charcoal-muted">
                            <Calendar className="w-4 h-4" />
                            <span className="whitespace-nowrap">{formatDate(donation.completedAt || donation.createdAt)}</span>
                          </div>
                        </div>
                        {donation.donationType && (
                          <div className="mt-2">
                            <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                              donation.donationType === 'recurring'
                                ? 'bg-gold/15 text-gold'
                                : donation.donationType === 'roundup'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-gold/10 text-charcoal-muted'
                            }`}>
                              {donation.donationType === 'recurring' ? 'Recurring' : donation.donationType === 'roundup' ? 'Round-Up' : 'One-Time'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {!showAllDonations && donations.length > VISIBLE_DONATION_COUNT && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
              )}
            </div>

            {donations.length > VISIBLE_DONATION_COUNT && (
              <button
                onClick={() => setShowAllDonations(!showAllDonations)}
                className="w-full mt-3 py-2 text-sm font-semibold text-gold hover:text-gold/80 transition-colors flex items-center justify-center gap-1.5"
              >
                {showAllDonations ? (
                  <><ChevronUp className="w-4 h-4" />Show less</>
                ) : (
                  <><ChevronDown className="w-4 h-4" />Show all {donationsMeta?.totalItems ?? donations.length} donations</>
                )}
              </button>
            )}

            {showAllDonations && donationsMeta && donationsMeta.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-surface-border">
                <div className="text-sm text-charcoal-muted">
                  Showing {((donationsMeta.page - 1) * donationsMeta.limit) + 1}–
                  {Math.min(donationsMeta.page * donationsMeta.limit, donationsMeta.totalItems)} of{' '}
                  {donationsMeta.totalItems}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setDonationsPage(p => Math.max(1, p - 1))} disabled={!donationsMeta.hasPrevPage || donationsLoading}>Previous</Button>
                  <Button variant="outline" size="sm" onClick={() => setDonationsPage(p => p + 1)} disabled={!donationsMeta.hasNextPage || donationsLoading}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <YearEndStatements />
    </div>
  )
}
